// @deprecated – replaced by PermitForm

import { useMemo } from 'react';
import { useJpa, useRequest } from '@stores';
import { connect } from 'redux-bundler-react';

import GPFormInstructions from '@forms/static-forms/GPForm2bInstructions';
import SupportingInformation from '@forms/input-forms/common/SupportingInformation';
import Contacts from '@forms/input-forms/common/Contacts';
import GeneralProjectInformation from '@forms/input-forms/common/GeneralProjectInformation';
import PermitInformation from '@forms/input-forms/PermitsForm/generalProjectInformation/PcnPermitInformation';
import PermitsFormOtherLawsRegulations from '@forms/input-forms/PermitsForm/otherLawsAndRegulations/PermitsFormOtherLawsRegulations';
import { PermitFormAquaticResources } from '@forms/input-forms/common/AquaticResources';
import PermitFormImpactsMitigation from '@forms/input-forms/PermitsForm/impactsAndMitigation/PermitsFormImpactsMitigation.old';
import PermitsFormCertifySignSubmit from '@forms/input-forms/PermitsForm/PermitsFormCertifySignSubmit';
import JpaInfo from './JpaInfo';
import JpaForm, { generateJpaFormName } from './JpaForm';

//List of steps for vertical stepper, these can also be used for form header. index = step ID
export const getGPForm2bMetadata = () => {
  const baseFormArray = [
    GPFormInstructions.metadata,
    Contacts.metadata,
    GeneralProjectInformation.metadata,
    PermitInformation.metadata,
    PermitsFormOtherLawsRegulations.metadata,
    PermitFormAquaticResources.metadata,
    PermitFormImpactsMitigation.metadata,
    SupportingInformation.metadata,
    PermitsFormCertifySignSubmit.metadata,
  ];
  const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
  const isJpa = jpaBasicInfo?.length > 0;

  // insert JPA sidebar steps
  if (isJpa) {
    const jpaInfoSteps = [
      {
        sectionName: 'Location Specific Information',
        isParentStep: true,
      },
    ];
    const jpaFormSteps = [
      {
        sectionName: 'Location Specific Questions',
        isParentStep: true,
      },
    ];

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: 1,
      });
      jpaFormSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: jpaBasicInfo?.length + baseFormArray.length - 1,
      });
    });

    baseFormArray.splice(1, 0, ...jpaInfoSteps);
    baseFormArray.splice(baseFormArray.length - 2, 0, ...jpaFormSteps);
  }

  return baseFormArray;
};

const generateSections = ({ doUpdatePermitRequest, isJpa, jpaBasicInfo, permitData, permitRequestID }) => {
  const sections = [
    {
      id: 'GP2bInstructions',
      name: 'Instructions',
      content: GPFormInstructions,
    },
    {
      id: 'GPFormContactInformation',
      name: 'Contact Information',
      content: Contacts,
      subtext:
        'requesting verification that the proposed activity is authorized by a Nationwide or other general permit.',
      isPermit: true,
      componentID: permitRequestID,
    },
    {
      id: 'GeneralProjectInformation',
      name: 'General Project Information',
      content: GeneralProjectInformation,
    },
    {
      id: 'PermitInformation',
      name: 'Permit Information',
      content: PermitInformation,
    },
    {
      id: 'GPOtherLawsAndRegulations',
      name: 'Other Laws And Regulations',
      content: PermitsFormOtherLawsRegulations,
    },
    {
      id: 'GPFormAquaticResources',
      name: 'Aquatic Resources',
      content: PermitFormAquaticResources,
    },
    {
      id: 'GPProjectImpactsAndMitigation',
      name: 'Project Impact and Mitigation',
      content: PermitFormImpactsMitigation,
    },
    {
      id: 'GPSupportingInformation',
      name: 'Supporting Information',
      content: SupportingInformation,
      componentID: permitRequestID,
      subtext: (
        <>
          <p>
            Please upload supporting documentation including project plans, state water certifications, etc. Multiple
            documents of the same file type can be combined and uploaded as one document.
          </p>
        </>
      ),
      updateRequestData: doUpdatePermitRequest,
    },
    {
      id: 'GP2bCertifySignSubmit',
      name: 'Certify Sign Submit',
      content: PermitsFormCertifySignSubmit,
    },
  ];

  // insert JPA content
  if (isJpa) {
    const jpaInfoSections = [
      {
        id: 'jpa-info',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];
    const jpaFormSections = [
      {
        id: 'jpa-questions',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];

    const infoStartIndex = 1;
    const formStartIndex = sections.length + jpaBasicInfo.length - 1;
    const infoGroupIndexes = [];
    const formGroupIndexes = [];

    jpaBasicInfo.forEach((_, jpaIndex) => {
      infoGroupIndexes.push(jpaIndex + infoStartIndex + 1);
      formGroupIndexes.push(jpaIndex + formStartIndex + 1);
    });

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSections.push({
        id: `jpa-info-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaInfo,
        componentId: permitRequestID,
        formId: jpa.formID,
        group: {
          indexes: infoGroupIndexes,
          parentIndex: infoStartIndex,
          parentName: jpaInfoSections[0].name,
        },
        requestId: permitData.requestID,
        version: permitData.version,
      });
      jpaFormSections.push({
        id: `jpa-questions-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaForm,
        componentId: permitRequestID,
        formId: jpa.formID,
        group: {
          indexes: formGroupIndexes,
          parentIndex: formStartIndex,
          parentName: jpaFormSections[0].name,
        },
        requestId: permitData.requestID,
        version: permitData.version,
      });
    });

    sections.splice(1, 0, ...jpaInfoSections);
    sections.splice(sections.length - 2, 0, ...jpaFormSections);
  }

  return sections;
};

const GPForm2b = connect(
  'doUpdatePermitRequest',
  'selectPermitRequestID',
  'selectPermitData',
  ({ doUpdatePermitRequest, permitRequestID, permitData }) => {
    const { getActiveSection } = useRequest();
    const { jpaBasicInfo } = useJpa();
    const isJpa = jpaBasicInfo?.length > 0;
    const activeStep = getActiveSection();
    const sections = useMemo(
      () => generateSections({ doUpdatePermitRequest, isJpa, jpaBasicInfo, permitData, permitRequestID }),
      [doUpdatePermitRequest, isJpa, jpaBasicInfo, permitData, permitRequestID]
    );

    return sections.map((section, index) => {
      const { content: Content, ...props } = section;

      return (
        <section
          id={section.id}
          key={section.id}
          name={section.name}
          style={{ display: activeStep?.id === index ? 'inline' : 'none' }}
        >
          <Content stepNo={index} {...props} {...section.props} />
        </section>
      );
    });
  }
);

export default GPForm2b;
